import React, { useEffect, useMemo, useRef, useState } from "react";
import Select from "react-select";
import { useDebounce } from "components/hooks/useDebounce";
import { ApiOptionList, ApiResponse, SelectOption } from "types/api.types";
import { useInsideTableFilterOverlay } from "./FilterMenuPortalContext";

/** Above `.table-filters-overlay-menu` (z-index 1060) and Bootstrap modals. */
const SELECT_MENU_PORTAL_Z_INDEX = 10050;

/** Scroll lives in the menu list, not in the filter panel scroll area. */
const SELECT_MAX_MENU_HEIGHT_IN_FILTER_OVERLAY = 240;

type NormalizedResult = {
  data: SelectOption[];
  hasMore: boolean;
};

type AsyncPaginatedSelectProps<T extends SelectOption> = {
  value: string | number | (string | number)[] | null;
  onChange: (option: T | T[] | null) => void;

  enabled?: boolean;
  requestParams?: Record<string, any>;
  isMulti?: boolean;

  onBlur?: () => void;
  defaultOption?: T | T[] | null;

  excludeIds?: number[];
  placeholder?: string;
  isDisabled?: boolean;
  isInvalid?: boolean;

  limit?: number;
  debounceMs?: number;

  fetchOptions: (
    params: {
      query: string;
      page: number;
      limit: number;
    } & Record<string, any>,
  ) => Promise<ApiResponse<ApiOptionList>>;

  components?: any;
  styles?: Record<string, (base: any, props?: any) => any>;
  menuPosition?: "fixed" | "absolute";
  menuPlacement?: "auto" | "top" | "bottom";
};

export function AsyncPaginatedSelect<T extends SelectOption>({
  value,
  onChange,
  requestParams,
  enabled = true,
  isMulti = false,
  onBlur,
  placeholder = "Search...",
  isDisabled,
  defaultOption,
  excludeIds,
  isInvalid,
  limit = 20,
  debounceMs = 400,
  components,
  styles: styleOverrides,
  fetchOptions,
  menuPosition = "fixed",
  menuPlacement = "auto",
}: AsyncPaginatedSelectProps<T>) {
  const inTableFilterOverlay = useInsideTableFilterOverlay();
  const [options, setOptions] = useState<SelectOption[]>([]);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, debounceMs);

  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);

  const [selectedCache, setSelectedCache] = useState<SelectOption[]>([]);

  const requestIdRef = useRef(0);
  const prevParamsRef = useRef<string>("");

  const resetOptionsState = () => {
    setOptions([]);
    setPage(1);
    setHasMore(true);
  };

  const refreshOptions = () => {
    resetOptionsState();
    setReloadTick((v) => v + 1);
  };

  /* ---------------- RESET WHEN SEARCH CHANGES ---------------- */

  useEffect(() => {
    resetOptionsState();
  }, [debouncedSearch]);

  /* ---------------- RESET WHEN ENABLED CHANGES ---------------- */

  useEffect(() => {
    if (!enabled) return;

    resetOptionsState();
  }, [enabled]);

  /* ---------------- RESET WHEN PARAMS CHANGE ---------------- */

  useEffect(() => {
    const currentKey = JSON.stringify(requestParams ?? {});

    if (prevParamsRef.current !== currentKey) {
      prevParamsRef.current = currentKey;

      resetOptionsState();
    }
  }, [requestParams]);

  /* ---------------- LOAD OPTIONS ---------------- */

  useEffect(() => {
    if (!enabled) return;
    if (!hasMore) return;

    const currentRequestId = ++requestIdRef.current;

    const load = async () => {
      try {
        setLoading(true);

        const res = await fetchOptions({
          query: debouncedSearch,
          page,
          limit,
          ...requestParams,
        });

        if (currentRequestId !== requestIdRef.current) return;

        const rows = Array.isArray(res.data.rows) ? res.data.rows : [];

        const normalized: NormalizedResult = {
          data: rows,
          hasMore:
            "hasMore" in res.data ? res.data.hasMore : rows.length === limit,
        };

        const filteredRows = excludeIds?.length
          ? normalized.data.filter(
              (item) => !excludeIds.includes(Number(item.value)),
            )
          : normalized.data;

        const defaults = defaultOption
          ? Array.isArray(defaultOption)
            ? defaultOption
            : [defaultOption]
          : [];

        setOptions((prev) => {
          const base = page === 1 ? filteredRows : [...prev, ...filteredRows];

          const existing = new Set(base.map((o) => String(o.value)));

          const missing = defaults.filter(
            (d) => !existing.has(String(d.value)),
          );

          return missing.length ? [...missing, ...base] : base;
        });

        setHasMore(normalized.hasMore);
      } catch (err) {
        console.error("AsyncPaginatedSelect error:", err);
      } finally {
        if (currentRequestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    };

    load();
  }, [
    enabled,
    debouncedSearch,
    page,
    limit,
    fetchOptions,
    excludeIds,
    requestParams,
    defaultOption,
    hasMore,
    reloadTick,
  ]);

  /* ---------------- CACHE SELECTED OPTIONS ---------------- */

  useEffect(() => {
    if (value == null || (Array.isArray(value) && value.length === 0)) {
      setSelectedCache([]);
      return;
    }

    const valuesArray = Array.isArray(value) ? value : [value];

    const matched = options.filter((o) =>
      valuesArray.some((v) => String(v) === String(o.value)),
    );

    if (matched.length) {
      setSelectedCache((prev) => {
        const map = new Map(prev.map((o) => [String(o.value), o]));
        matched.forEach((m) => map.set(String(m.value), m));
        return Array.from(map.values());
      });
    }
  }, [options, value]);

  /* ---------------- RESOLVE SELECTED OPTION ---------------- */

  const selectedOption = useMemo(() => {
    if (value == null) return isMulti ? [] : null;

    const valuesArray = Array.isArray(value) ? value : [value];

    const defaults = defaultOption
      ? Array.isArray(defaultOption)
        ? defaultOption
        : [defaultOption]
      : [];

    const source = [...defaults, ...selectedCache, ...options];

    const unique = new Map(source.map((o) => [String(o.value), o]));

    const matched = valuesArray
      .map((v) => unique.get(String(v)))
      .filter(Boolean) as SelectOption[];

    return isMulti ? matched : (matched[0] ?? null);
  }, [options, selectedCache, value, isMulti, defaultOption]);

  /* ---------------- HANDLE CHANGE ---------------- */

  const handleChange = (selected: any) => {
    const wasCleared =
      selected == null || (Array.isArray(selected) && selected.length === 0);

    if (wasCleared) {
      setSearch("");
      setSelectedCache([]);
      resetOptionsState();
    }

    if (isMulti) {
      onChange((selected ?? []) as T[]);
    } else {
      onChange((selected ?? null) as T | null);
    }
  };

  /* ---------------- RENDER ---------------- */

  return (
    <Select
      isMulti={isMulti}
      value={selectedOption}
      components={components}
      options={options}
      isLoading={loading}
      isDisabled={isDisabled || !enabled}
      isClearable
      placeholder={placeholder}
      onInputChange={(val: any, meta: any) => {
        if (meta.action === "input-change") {
          setSearch(val);
        } else if (meta.action === "set-value" || meta.action === "menu-close") {
          setSearch("");
        }
      }}
      onChange={handleChange}
      onBlur={onBlur}
      onFocus={refreshOptions}
      onMenuOpen={refreshOptions}
      onMenuScrollToBottom={() => {
        if (hasMore && !loading) {
          setPage((p) => p + 1);
        }
      }}
      {...(inTableFilterOverlay
        ? { maxMenuHeight: SELECT_MAX_MENU_HEIGHT_IN_FILTER_OVERLAY }
        : {})}
      className={
        isInvalid
          ? "border border-danger rounded-2 is-invalid"
          : inTableFilterOverlay
            ? "w-100"
            : ""
      }
      styles={{
        container: (provided: any) => ({
          ...provided,
          width: "100%",
          maxWidth: "100%",
          ...(styleOverrides?.container ? styleOverrides.container(provided) : {}),
        }),
        control: (provided: any, state: any) => ({
          ...provided,
          backgroundColor: "var(--vz-secondary-bg)",
          borderColor: isInvalid
            ? "var(--vz-danger)"
            : state.isFocused
              ? "var(--vz-primary)"
              : "var(--vz-border-color)",
          boxShadow: state.isFocused
            ? isInvalid
              ? "0 0 0 0.25rem rgba(var(--vz-danger-rgb),0.25)"
              : "0 0 0 0.25rem rgba(var(--vz-primary-rgb),0.25)"
            : "none",
          "&:hover": {
            borderColor: isInvalid ? "var(--vz-danger)" : "var(--vz-primary)",
          },
          ...(inTableFilterOverlay ? { minWidth: 0 } : {}),
          ...(styleOverrides?.control ? styleOverrides.control(provided, state) : {}),
        }),
        menu: (provided: any) => ({
          ...provided,
          backgroundColor: "var(--vz-secondary-bg)",
          border: "1px solid var(--vz-border-color)",
          zIndex: SELECT_MENU_PORTAL_Z_INDEX,
          ...(styleOverrides?.menu ? styleOverrides.menu(provided) : {}),
        }),
        menuList: (provided: any) => ({
          ...provided,
          ...(styleOverrides?.menuList ? styleOverrides.menuList(provided) : {}),
        }),
        option: (provided: any, state: any) => ({
          ...provided,
          backgroundColor: state.isSelected
            ? "var(--vz-primary)"
            : state.isFocused
              ? "rgba(var(--vz-primary-rgb),0.1)"
              : "transparent",
          color: state.isSelected ? "#fff" : "var(--vz-body-color)",
          "&:active": { backgroundColor: "var(--vz-primary)", color: "#fff" },
          ...(styleOverrides?.option ? styleOverrides.option(provided, state) : {}),
        }),
        singleValue: (provided: any) => ({
          ...provided,
          color: "var(--vz-body-color)",
          ...(styleOverrides?.singleValue ? styleOverrides.singleValue(provided) : {}),
        }),
        input: (provided: any) => ({
          ...provided,
          color: "var(--vz-body-color)",
          ...(styleOverrides?.input ? styleOverrides.input(provided) : {}),
        }),
        placeholder: (provided: any) => ({
          ...provided,
          color: "var(--vz-secondary-color)",
          ...(styleOverrides?.placeholder ? styleOverrides.placeholder(provided) : {}),
        }),
        multiValue: (provided: any) => ({
          ...provided,
          backgroundColor: "var(--vz-primary)",
          ...(styleOverrides?.multiValue ? styleOverrides.multiValue(provided) : {}),
        }),
        multiValueLabel: (provided: any) => ({
          ...provided,
          color: "#fff",
          ...(styleOverrides?.multiValueLabel ? styleOverrides.multiValueLabel(provided) : {}),
        }),
        multiValueRemove: (provided: any) => ({
          ...provided,
          color: "#fff",
          "&:hover": { backgroundColor: "rgba(0,0,0,0.2)", color: "#fff" },
          ...(styleOverrides?.multiValueRemove ? styleOverrides.multiValueRemove(provided) : {}),
        }),
        indicatorSeparator: (provided: any) => ({
          ...provided,
          backgroundColor: "var(--vz-border-color)",
          ...(styleOverrides?.indicatorSeparator ? styleOverrides.indicatorSeparator(provided) : {}),
        }),
        dropdownIndicator: (provided: any) => ({
          ...provided,
          color: "var(--vz-secondary-color)",
          "&:hover": { color: "var(--vz-body-color)" },
          ...(styleOverrides?.dropdownIndicator ? styleOverrides.dropdownIndicator(provided) : {}),
        }),
        clearIndicator: (provided: any) => ({
          ...provided,
          color: "var(--vz-secondary-color)",
          "&:hover": { color: "var(--vz-danger)" },
          ...(styleOverrides?.clearIndicator ? styleOverrides.clearIndicator(provided) : {}),
        }),
        noOptionsMessage: (provided: any) => ({
          ...provided,
          color: "var(--vz-secondary-color)",
          ...(styleOverrides?.noOptionsMessage ? styleOverrides.noOptionsMessage(provided) : {}),
        }),
        loadingMessage: (provided: any) => ({
          ...provided,
          color: "var(--vz-secondary-color)",
          ...(styleOverrides?.loadingMessage ? styleOverrides.loadingMessage(provided) : {}),
        }),
        menuPortal: (base: any) => ({
          ...base,
          zIndex: SELECT_MENU_PORTAL_Z_INDEX,
          ...(styleOverrides?.menuPortal ? styleOverrides.menuPortal(base) : {}),
        }),
      }}
      menuPortalTarget={
        typeof document !== "undefined" ? document.body : undefined
      }
      menuPosition={menuPosition}
      menuPlacement={menuPlacement}
    />
  );
}
